import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import { AuthContext } from '../../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import TrustpageService from '../../../services/TrustpageService';
import Section from '../../../components/Main/Section/Section';
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';
import { Add } from '@mui/icons-material';
import ContentContainer from '../../../components/Containers/ContentContainer';
import ActionBar from '../../../components/ActionBar/ActionBar';
import TrustpageEntry from '../../../components/TrustpageEntry/TrustpageEntry';
import { IStoredTrustpage } from '../../../shared/interfaces/IStoredTrustpage';
import LeftContainer from '../../../components/Containers/LeftContainer';

const Trustpages: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [trustpages, setTrustpages] = useState<IStoredTrustpage[]>([]);

  useEffect(() => {
    if (!hasRole('can-view-trustpage')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    setCurrentView(t('trustpage'));
    document.title = `Reputativ ${t('trustpage')}`;
  }, [setCurrentView, t]);

  const reload = async () => {
    await getTrustpages(new AbortController());
  };

  const getTrustpages = async (controller: AbortController) => {
    const trustpages = await TrustpageService.getTrustpages(setLoading, controller.signal);

    if (!trustpages) return;
    setTrustpages(trustpages);
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => await getTrustpages(controller))();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Section>
      <>
        {trustpages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <img
              width={300}
              src={MonitoringNoContent}
              alt="Trustpage no records"
            />
            <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
            {hasRole('can-add-trustpage') && !exceedsLimit('limittrustpages', 1) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('guide')}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('create_trustpage_caption')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <ContentContainer>
                <LeftContainer>
                  <ActionBar>
                    {hasRole('can-add-trustpage') &&
                      !exceedsLimit('limittrustpages', trustpages.length + 1) && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('guide')}
                          sx={{ ml: 'auto' }}
                        >
                          {t('create_trustpage_caption')}
                        </Button>
                      )}
                  </ActionBar>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: theme.spacing(1),
                      mt: theme.spacing(2),
                    }}
                  >
                    {trustpages.map((trustpage) => {
                      return (
                        <TrustpageEntry
                          key={trustpage.id}
                          trustpage={trustpage}
                          trustpages={trustpages}
                          customFontSet={trustpage.customFontSet}
                          companyLogoSet={trustpage.companyLogoSet}
                          reload={reload}
                        />
                      );
                    })}
                  </Box>
                </LeftContainer>
              </ContentContainer>
            )}
          </>
        )}
      </>
    </Section>
  );
};

export default Trustpages;
